import type { DocumentModelGlobalState } from "document-model";

export const documentModel: DocumentModelGlobalState = {
  id: "powerhouse/package",
  name: "Vetra Package",
  author: {
    name: "Powerhouse",
    website: "https://powerhouse.inc",
  },
  extension: ".pkg",
  description:
    "Manifest for a Vetra Reactor Package: bundles the metadata (name, description, category, author, keywords, source links) used to publish and discover the package in the Powerhouse ecosystem. Create one Vetra Package document per project; the document models, editors, processors, and subgraphs you add to the project are registered against this package at build time.",
  specifications: [
    {
      state: {
        local: {
          schema: "",
          examples: [],
          initialValue: "",
        },
        global: {
          schema:
            '"""\nPackage metadata used to publish a Vetra Reactor Package to npm and surface\nit inside Connect/Switchboard. All fields are optional so a package can be\ncreated empty and filled in incrementally during development.\n"""\ntype VetraPackageState {\n  """Human-readable package name (e.g. \'Pizza Plaza\'). Distinct from the npm package id."""\n  name: String\n  """One-paragraph summary of what the package provides. Shown on package listing pages."""\n  description: String\n  """Free-form category label used to group packages in directories (e.g. \'Finance\', \'Productivity\')."""\n  category: String\n  """Author/maintainer information surfaced in the published package metadata."""\n  author: Author!\n  """Search keywords associated with the package. Each keyword has a stable id so it can be removed individually."""\n  keywords: [Keyword!]!\n  """Public source code URL (typically a GitHub repository)."""\n  githubUrl: URL\n  """Published npm package URL. Set once the package has been released."""\n  npmUrl: URL\n}\n\n"""Author/maintainer of the Vetra Package."""\ntype Author {\n  """Display name of the author or organization."""\n  name: String\n  """Author\'s website URL."""\n  website: URL\n}\n\n"""A single search keyword attached to the package."""\ntype Keyword {\n  """Stable identifier for the keyword entry; used to remove it."""\n  id: OID!\n  """Display label for the keyword (e.g. \'invoicing\', \'defi\')."""\n  label: String!\n}',
          examples: [],
          initialValue:
            '{\n  "name": null,\n  "description": null,\n  "category": null,\n  "author": {\n    "name": null,\n    "website": null\n  },\n  "keywords": [],\n  "githubUrl": null,\n  "npmUrl": null\n}',
        },
      },
      modules: [
        {
          id: "a6156f32-8120-43a5-be8b-51c7feaa3460",
          name: "base_operations",
          description:
            "Setters for the package's identity, author, keywords, and source/distribution links.",
          operations: [
            {
              id: "33f2eab7-9e07-497f-a4e6-53e50968c3c9",
              name: "SET_PACKAGE_NAME",
              description:
                "Set the human-readable package name shown in listings and the Connect UI.",
              schema: "input SetPackageNameInput {\n  name: String!\n}",
              template: "",
              reducer: "",
              errors: [],
              examples: [],
              scope: "global",
            },
            {
              id: "a07bee80-c2a8-40d7-891b-822ee298d7d9",
              name: "SET_PACKAGE_DESCRIPTION",
              description:
                "Set the one-paragraph summary describing what the package does.",
              schema:
                "input SetPackageDescriptionInput {\n  description: String!\n}",
              template: "",
              reducer: "",
              errors: [],
              examples: [],
              scope: "global",
            },
            {
              id: "f3ad82f8-580a-4c6e-b930-e203cda998bb",
              name: "SET_PACKAGE_CATEGORY",
              description:
                'Set the category label used to group the package in directories (e.g. "Finance", "Productivity").',
              schema: "input SetPackageCategoryInput {\n  category: String!\n}",
              template: "",
              reducer: "",
              errors: [],
              examples: [],
              scope: "global",
            },
            {
              id: "67f43579-ce98-47e6-bf48-27c0f44818b9",
              name: "SET_PACKAGE_AUTHOR",
              description:
                "Replace the author block in a single call. Either field may be omitted to clear it.",
              schema:
                "input SetPackageAuthorInput {\n  name: OID\n  website: URL\n}",
              template: "",
              reducer: "",
              errors: [],
              examples: [],
              scope: "global",
            },
            {
              id: "94ba44c4-9627-405e-9ea2-34e9da1c283a",
              name: "SET_PACKAGE_AUTHOR_NAME",
              description:
                "Set only the author's display name without touching the website field.",
              schema: "input SetPackageAuthorNameInput {\n  name: String!\n}",
              template: "",
              reducer: "",
              errors: [],
              examples: [],
              scope: "global",
            },
            {
              id: "3d5c08df-6c14-480b-915b-875941979fd0",
              name: "SET_PACKAGE_AUTHOR_WEBSITE",
              description:
                "Set only the author's website URL without touching the name field.",
              schema:
                "input SetPackageAuthorWebsiteInput {\n  website: URL!\n}",
              template: "",
              reducer: "",
              errors: [],
              examples: [],
              scope: "global",
            },
            {
              id: "ed95f841-6fd0-4552-898d-e915599b7495",
              name: "ADD_PACKAGE_KEYWORD",
              description:
                "Append a search keyword. Caller supplies a stable id so the entry can be removed later.",
              schema:
                "input AddPackageKeywordInput {\n  id: String!\n  label: String!\n}",
              template: "",
              reducer: "",
              errors: [],
              examples: [],
              scope: "global",
            },
            {
              id: "83c4db24-bd2f-424d-9e26-751580d6307b",
              name: "REMOVE_PACKAGE_KEYWORD",
              description:
                "Remove a keyword by its id. No-op if the id does not exist.",
              schema: "input RemovePackageKeywordInput {\n  id: String!\n}",
              template: "",
              reducer: "",
              errors: [],
              examples: [],
              scope: "global",
            },
            {
              id: "83a90530-8535-4ea7-ab53-7865532398f7",
              name: "SET_PACKAGE_GITHUB_URL",
              description:
                "Set the public source code URL (typically a GitHub repository).",
              schema: "input SetPackageGithubUrlInput {\n  url: URL!\n}",
              template: "",
              reducer: "",
              errors: [],
              examples: [],
              scope: "global",
            },
            {
              id: "e789859a-01ee-4b60-8e7e-a3ad928a26d1",
              name: "SET_PACKAGE_NPM_URL",
              description:
                "Set the published npm package URL once the package has been released.",
              schema: "input SetPackageNpmUrlInput {\n  url: URL!\n}",
              template: "",
              reducer: "",
              errors: [],
              examples: [],
              scope: "global",
            },
          ],
        },
      ],
      version: 1,
      changeLog: [],
    },
  ],
};
